import { SPECS } from "@/lib/sim/catalog";
import type { Comp, CompState } from "@/lib/sim/engine";
import { X } from "lucide-react";

const COIL_VOLTAGES = ["24V DC", "24V AC", "48V AC", "110V AC", "220V AC", "380V AC"];

function msParts(ms: number) {
  return {
    min: Math.floor(ms / 60000),
    sec: Math.floor((ms % 60000) / 1000),
    frac: Math.round((ms % 1000) / 100),
  };
}

export function Inspector({
  comp,
  comps,
  running,
  onPatch,
  onRename,
  onClose,
}: {
  comp: Comp;
  comps: Comp[];
  running: boolean;
  onPatch: (s: Partial<CompState>) => void;
  onRename: (label: string) => void;
  onClose: () => void;
}) {
  const spec = SPECS[comp.type];
  if (!spec) return null;

  const isTimer = spec.delay !== undefined;
  const isAdjustableSource = comp.type === "src_3ph" || comp.type === "src_1ph";
  const isProtection =
    comp.type === "phase_failure" ||
    comp.type === "voltage_monitor";

  const sourceVoltage =
    comp.state.sourceVoltage ?? spec.sourceVoltage ??
    (comp.type === "src_3ph" ? 400 : 230);
  const sourceMin = comp.type === "src_3ph" ? 250 : 120;
  const sourceMax = comp.type === "src_3ph" ? 500 : 300;

  const protectionMinVoltage =
    comp.state.minVoltage ?? spec.protectionMinVoltage ??
    (comp.type === "voltage_monitor" ? 190 : 340);
  const protectionMaxVoltage =
    comp.state.maxVoltage ?? spec.protectionMaxVoltage ??
    (comp.type === "voltage_monitor" ? 250 : 440);
  const protectionDelayMs =
    comp.state.protectionDelay ?? (spec.protectionDelay ?? 3) * 1000;

  const delayMs = comp.state.delay ?? (spec.delay ?? 0) * 1000;
  const p = msParts(delayMs);
  const setDelay = (min: number, sec: number, frac: number) =>
    onPatch({ delay: Math.max(100, min * 60000 + sec * 1000 + frac * 100) });

  // العناصر التي يمكن للنقاط المساعدة أن تتبعها (ملف)
  const masters = comps.filter((c) => c.id !== comp.id && SPECS[c.type]?.coil);

  const field = "w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs outline-none focus:border-primary";

  return (
    <div className="absolute top-3 end-3 z-10 w-64 rounded-lg border border-border bg-card/95 p-3 text-xs shadow-lg backdrop-blur">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="font-bold">خصائص: {spec.name}</div>
        <button onClick={onClose} aria-label="إغلاق" className="rounded p-1 hover:bg-accent">
          <X className="size-3.5" />
        </button>
      </div>

      <label className="mb-2 block">
        <span className="mb-1 block text-[10px] text-muted-foreground">
          التسمية / الرمز (Tag) — مثل KM1 أو «كونتكتر المضخة»
        </span>
        <input
          value={comp.label}
          onChange={(e) => onRename(e.target.value)}
          className={field}
          dir="auto"
        />
      </label>

      {spec.coil && (
        <label className="mb-2 block">
          <span className="mb-1 block text-[10px] text-muted-foreground">فولتية الملف Coil</span>
          <select
            value={comp.state.coilVoltage ?? spec.coilVoltage ?? "220V AC"}
            onChange={(e) => onPatch({ coilVoltage: e.target.value })}
            className={field}
          >
            {COIL_VOLTAGES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </label>
      )}

      {isAdjustableSource && (
        <div className="mb-2 rounded-md border border-border p-2">
          <div className="mb-1 flex items-center justify-between gap-2">
            <span className="text-[10px] text-muted-foreground">فولتية المصدر</span>
            <strong className="text-sm">{Math.round(sourceVoltage)} V</strong>
          </div>
          <input
            type="range"
            min={sourceMin}
            max={sourceMax}
            step={1}
            value={sourceVoltage}
            onChange={(e) => onPatch({ sourceVoltage: Number(e.target.value) })}
            className="w-full accent-primary"
          />
          <div className="mt-1 flex items-center gap-2">
            <input
              type="number"
              min={sourceMin}
              max={sourceMax}
              step={1}
              value={sourceVoltage}
              onChange={(e) =>
                onPatch({
                  sourceVoltage: Math.max(
                    sourceMin,
                    Math.min(sourceMax, Number(e.target.value)),
                  ),
                })
              }
              className={field}
            />
            <span className="whitespace-nowrap text-[10px] text-muted-foreground">V</span>
          </div>
          <div className="mt-1 text-[10px] text-muted-foreground">
            {comp.type === "src_3ph"
              ? "ثلاثي الطور: القيمة خط-إلى-خط"
              : "أحادي الطور: القيمة فاز-إلى-نيوترال"}
          </div>
        </div>
      )}

      {isProtection && (
        <div className="mb-2 rounded-md border border-border p-2">
          <div className="mb-2 text-[10px] font-semibold">إعدادات الحماية</div>
          <div className="grid grid-cols-2 gap-1.5">
            <label>
              <span className="mb-1 block text-[10px] text-muted-foreground">U&lt; انخفاض</span>
              <input
                type="number"
                min={0}
                step={1}
                value={protectionMinVoltage}
                onChange={(e) => onPatch({ minVoltage: Number(e.target.value) })}
                className={field}
              />
            </label>
            <label>
              <span className="mb-1 block text-[10px] text-muted-foreground">U&gt; ارتفاع</span>
              <input
                type="number"
                min={0}
                step={1}
                value={protectionMaxVoltage}
                onChange={(e) => onPatch({ maxVoltage: Number(e.target.value) })}
                className={field}
              />
            </label>
          </div>

          <label className="mt-2 block">
            <span className="mb-1 block text-[10px] text-muted-foreground">زمن تشغيل OUT</span>
            <input
              type="number"
              min={0}
              max={60}
              step={0.1}
              value={(protectionDelayMs / 1000).toFixed(1)}
              onChange={(e) =>
                onPatch({
                  protectionDelay: Math.max(0, Number(e.target.value) * 1000),
                  protectionElapsed: 0,
                  protectionActive: false,
                })
              }
              className={field}
            />
          </label>

          <div className="mt-2 rounded bg-muted/40 p-1.5 text-[10px] leading-relaxed">
            القراءة: <b>{Math.round(comp.state.measuredVoltage ?? 0)} V</b>
            {running && (
              <>
                {" · "}
                {comp.state.protectionInputPowered
                  ? comp.state.protectionFault
                    ? "FAULT"
                    : comp.state.protectionActive
                      ? "OUT ON"
                      : `WAIT ${((comp.state.protectionElapsed ?? 0) / 1000).toFixed(1)}s`
                  : "NO POWER"}
              </>
            )}
          </div>
        </div>
      )}

      {isTimer && (
        <div className="mb-2 rounded-md border border-border p-2">
          <div className="mb-1 text-[10px] text-muted-foreground">
            زمن التأخير ({spec.timerMode === "off" ? "تأخير فصل" : "تأخير عمل"})
          </div>
          <div className="flex items-end gap-1.5">
            <label className="flex-1">
              <span className="block text-[10px]">دقيقة</span>
              <input
                type="number"
                min={0}
                max={60}
                value={p.min}
                onChange={(e) => setDelay(Number(e.target.value), p.sec, p.frac)}
                className={field}
              />
            </label>
            <label className="flex-1">
              <span className="block text-[10px]">ثانية</span>
              <input
                type="number"
                min={0}
                max={59}
                value={p.sec}
                onChange={(e) => setDelay(p.min, Number(e.target.value), p.frac)}
                className={field}
              />
            </label>
            <label className="flex-1">
              <span className="block text-[10px]">جزء/10</span>
              <input
                type="number"
                min={0}
                max={9}
                value={p.frac}
                onChange={(e) => setDelay(p.min, p.sec, Number(e.target.value))}
                className={field}
              />
            </label>
          </div>
          <div className="mt-1 text-[10px] text-muted-foreground">
            الزمن الكلي: {(delayMs / 1000).toFixed(1)} ثانية
            {running && (
              <>
                {" · "}المنقضي: {((comp.state.elapsed ?? 0) / 1000).toFixed(1)} ث
              </>
            )}
          </div>
        </div>
      )}

      {spec.linkable && (
        <label className="mb-2 block">
          <span className="mb-1 block text-[10px] text-muted-foreground">
            تابعة للعنصر (تتغير حالتها مع ملفه)
          </span>
          <select
            value={comp.state.linkTo ?? ""}
            onChange={(e) => onPatch({ linkTo: e.target.value })}
            className={field}
          >
            <option value="">— غير مرتبطة —</option>
            {masters.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label} — {SPECS[m.type]?.name}
              </option>
            ))}
          </select>
          {!masters.length && (
            <span className="mt-1 block text-[10px] text-destructive">
              أضف كونتكتر أو ريليه أو تايمر أولاً لتربط النقطة به.
            </span>
          )}
        </label>
      )}

      {spec.positions && (
        <label className="mb-2 block">
          <span className="mb-1 block text-[10px] text-muted-foreground">وضعية المفتاح</span>
          <select
            value={comp.state.pos ?? 0}
            onChange={(e) => onPatch({ pos: Number(e.target.value) })}
            className={field}
          >
            {spec.positions.map((n, i) => (
              <option key={n} value={i}>
                {n}
              </option>
            ))}
          </select>
        </label>
      )}

      {spec.info && (
        <p className="text-[10px] leading-relaxed text-muted-foreground">{spec.info}</p>
      )}
    </div>
  );
}
